import { Alert, AlertIcon, AlertTitle, AlertDescription, Box } from '@chakra-ui/react';
import moment from 'moment';

interface CampaignStatusBannerProps {
  status: string;
  isActive: boolean;
  expiresAt?: string | null;
  goalReached: boolean;
}

export function CampaignStatusBanner({ status, isActive, expiresAt, goalReached }: CampaignStatusBannerProps) {
  const isExpired = expiresAt ? new Date(expiresAt).getTime() < Date.now() : false;
  const daysUntilExpiration = expiresAt ? moment(expiresAt).diff(moment(), 'days') : null;
  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration > 0 && daysUntilExpiration <= 7;

  // Goal reached - show success
  if (goalReached) {
    return (
      <Alert status="success" borderRadius="xl" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Goal Reached!</AlertTitle>
          <AlertDescription>This campaign has reached its funding goal.</AlertDescription>
        </Box>
      </Alert>
    );
  }

  // Expired - show error
  if (isExpired) {
    return (
      <Alert status="error" borderRadius="xl" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Campaign Expired</AlertTitle>
          <AlertDescription>This campaign is no longer accepting contributions.</AlertDescription>
        </Box>
      </Alert>
    );
  }

  // Inactive/suspended - show warning
  if (!isActive || status.toUpperCase() !== 'ACTIVE') {
    return (
      <Alert status="warning" borderRadius="xl" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Campaign {status.toLowerCase() || 'inactive'}</AlertTitle>
          <AlertDescription>This campaign is not currently accepting contributions.</AlertDescription>
        </Box>
      </Alert>
    );
  }

  // Expiring soon - show info
  if (isExpiringSoon) {
    return (
      <Alert status="info" borderRadius="xl" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Ending Soon</AlertTitle>
          <AlertDescription>
            This campaign ends in {daysUntilExpiration} day{daysUntilExpiration === 1 ? '' : 's'}. Contribute now!
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return null; // Active campaign, no banner needed
}
